'use client';

import { useState, useCallback } from 'react';
import { getBrowserSupabaseClient } from '@wtc/supabase';
import { clearSupabaseCookies } from '../lib/utils/clear-cookies';

export interface UseGoogleOAuthOptions {
  /**
   * The redirect path after successful login (e.g., "/dashboard", "/contacts/dashboard")
   */
  redirectPath?: string;
  /**
   * Optional callback when sign-in starts
   */
  onSignInStart?: () => void;
  /**
   * Optional callback when sign-in completes
   */
  onSignInComplete?: () => void;
  /**
   * Optional callback when error occurs
   */
  onError?: (error: string) => void;
}

export function useGoogleOAuth(options: UseGoogleOAuthOptions = {}) {
  const {
    redirectPath = '/dashboard',
    onSignInStart,
    onSignInComplete,
    onError,
  } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const supabase = getBrowserSupabaseClient();

  const handleGoogleSignIn = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    onSignInStart?.();

    try {
      // Clear existing cookies before Google sign-in
      const clearResult = clearSupabaseCookies();
      console.log('Cleared cookies before Google sign-in:', clearResult);

      // Determine final destination and use server callback for cookie-based auth
      const redirectUrl = `${window.location.origin}/auth/callback?next=${encodeURIComponent(redirectPath)}`;

      // Sign in with Google using Supabase's built-in OAuth (browser client)
      const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (oauthError) {
        console.error('Google sign-in error:', oauthError);
        const errorMessage = oauthError.message || 'An error occurred during Google sign-in';
        setError(errorMessage);
        onError?.(errorMessage);
        return;
      }

      // The redirect will happen automatically
      // No need to set success message as user will be redirected
      onSignInComplete?.();
    } catch (err) {
      console.error('Unexpected Google sign-in error:', err);
      const errorMessage = 'An unexpected error occurred during Google sign-in';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [redirectPath, onSignInStart, onSignInComplete, onError, supabase.auth]);

  return {
    handleGoogleSignIn,
    isLoading,
    error,
    setError,
  };
}
